import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { mdiFileTreeOutline } from '@mdi/js'

import { AppIcon } from './ui/icon'
import { Button } from './ui/button'
import {
    AlertDialog,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogTrigger,
} from './ui/alert-dialog'
import { ApiSearchableSelect, type ApiSearchableSelectOption } from './ApiSearchableSelect'
import { useApi } from '../api/hooks'
import { useAssetCoreRows } from '../hooks/useAssetCoreRows'
import { useAssetDescendantUids } from '../hooks/useAssetDescendantUids'

interface AssetMoveDialogProps {
    org: string
    assetUid: string
    /** Current parent, only to seed the picker with a readable label. */
    parentAssetUid?: string | null
    parentAssetName?: string | null
    onMoved?: () => void
    /** Controlled mode: the caller (the header's « … » menu) owns the trigger. */
    open?: boolean
    onOpenChange?: (open: boolean) => void
}

/**
 * Moving an asset, back where people look for it.
 *
 * The control used to live in the page header and moved into the property
 * registry's identity rows (commit 9e0d6afa8). It was never removed, but in a
 * demonstration nobody finds it: you have to know to open the Data or
 * Properties tab and scroll to Identité. This is the header affordance again —
 * a dialog rather than the old inline editor, and deliberately NOT a second
 * save path: it calls the identity row's own `onSaveCoreRow`, so the two
 * cannot drift.
 */
export function AssetMoveDialog({ org, assetUid, parentAssetUid, parentAssetName, onMoved, open: openProp, onOpenChange }: AssetMoveDialogProps) {
    const { t } = useTranslation('assets')
    const { request } = useApi()
    const { onSaveCoreRow } = useAssetCoreRows(org, assetUid)
    const excluded = useAssetDescendantUids(org, assetUid)

    const [openState, setOpenState] = useState(false)
    const controlled = openProp !== undefined
    const open = controlled ? openProp : openState
    const setOpen = useCallback((o: boolean) => {
        if (!controlled) setOpenState(o)
        onOpenChange?.(o)
    }, [controlled, onOpenChange])
    const [saving, setSaving] = useState(false)
    const [error, setError] = useState<string | null>(null)

    // Same search and the same exclusions as the identity row.
    const search = useCallback(async (query: string): Promise<ApiSearchableSelectOption[]> => {
        try {
            const response = await request('/api/v1/organizations/{orgCode}/assets', 'GET', {
                params: { path: { orgCode: org }, query: { limit: '50', ...(query ? { q: query } : {}) } },
            })
            const assets = ((response as { data?: Array<Record<string, unknown>> })?.data) || []
            return assets
                .filter((a) => !excluded.has(String(a.uid)))
                .map((a) => ({ value: String(a.uid), label: String(a.name || a.code || a.uid) }))
        } catch {
            return []
        }
    }, [request, org, excluded])

    const save = useCallback(async (uid: string | null) => {
        setSaving(true)
        setError(null)
        try {
            await onSaveCoreRow('parent_asset_uid', uid)
            setOpen(false)
            onMoved?.()
        } catch (e) {
            setError(e instanceof Error ? e.message : String(e))
        } finally {
            setSaving(false)
        }
    }, [onSaveCoreRow, onMoved, setOpen])

    return (
        <AlertDialog open={open} onOpenChange={(o) => { setOpen(o); if (!o) setError(null) }}>
            {!controlled && (
                <AlertDialogTrigger asChild>
                    <Button
                        variant="outline"
                        size="icon"
                        data-testid="move-asset-btn"
                        aria-label={t('move.button')}
                        title={t('move.button')}
                    >
                        <AppIcon path={mdiFileTreeOutline} size="sm" />
                    </Button>
                </AlertDialogTrigger>
            )}
            <AlertDialogContent data-testid="move-asset-dialog">
                <AlertDialogHeader>
                    <AlertDialogTitle>{t('move.title')}</AlertDialogTitle>
                    <AlertDialogDescription>{t('move.description')}</AlertDialogDescription>
                </AlertDialogHeader>

                <ApiSearchableSelect
                    value={parentAssetUid || ''}
                    placeholder={t('move.placeholder')}
                    searchPlaceholder={t('move.search_placeholder')}
                    emptyMessage={t('move.no_match')}
                    onSearch={search}
                    initialOptions={parentAssetUid && parentAssetName
                        ? [{ value: parentAssetUid, label: parentAssetName }]
                        : []}
                    onValueChange={(uid) => save(uid || null)}
                    disabled={saving}
                    minSearchLength={2}
                    debounceMs={150}
                    data-testid="move-asset-picker"
                />

                {error && <p className="text-xs text-destructive" data-testid="move-asset-error">{error}</p>}

                <AlertDialogFooter>
                    <AlertDialogCancel data-testid="move-asset-cancel">{t('detail.cancel')}</AlertDialogCancel>
                    {/* Detaching to the root is a move like any other, and the
                        only one the picker cannot express. */}
                    <Button
                        variant="outline"
                        disabled={saving || !parentAssetUid}
                        onClick={() => save(null)}
                        data-testid="move-asset-to-root"
                    >
                        {t('move.detach_to_root')}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    )
}
